/**
 * Shared section content renderer with markdown formatting (**, *, ~~, __).
 * Use in both section editor preview and results/output views for consistency.
 */
import React from 'react';
import { preprocessSectionContentForPreview } from '@soVendor/utils/pdfTextSanitizer';
import { parseWorkExperienceContent, workExperienceEntriesToDisplayText } from '../utils/workExperienceFields';

export function isBulletLine(line: string): boolean {
  const t = line.trimStart();
  if (/^[•\-](\s|$)/.test(t)) return true;
  if (/^\*\s/.test(t)) return true;
  return false;
}

export function bulletContent(line: string): string {
  const t = line.trimStart();
  const m = t.match(/^[•*\-]\s*(.*)$/);
  return m ? m[1].trim() : t;
}

export function isNumberedLine(line: string): boolean {
  const t = line.trimStart();
  return /^\d+\.\s+/.test(t) || /^\d+\)\s+/.test(t);
}

const INLINE_MARKERS: { open: string; close: string; Wrapper: React.FC<{ children: React.ReactNode }> }[] = [
  { open: '~~', close: '~~', Wrapper: ({ children }) => <span className="line-through" style={{ textDecoration: 'line-through' }}>{children}</span> },
  { open: '__', close: '__', Wrapper: ({ children }) => <span className="underline" style={{ textDecoration: 'underline' }}>{children}</span> },
  { open: '**', close: '**', Wrapper: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong> },
  { open: '*', close: '*', Wrapper: ({ children }) => <em className="italic" style={{ fontStyle: 'italic' }}>{children}</em> },
];

/** Render inline markdown: **bold**, *italic*, ~~strikethrough~~, __underline__. Parses outside-in so nesting works. */
export function renderInlineFormatting(text: string, lineKey: string): React.ReactNode {
  if (text == null || typeof text !== 'string') return text ?? '';
  const safeLineKey = typeof lineKey === 'string' ? lineKey : 'L';
  let partIdx = 0;
  const key = () => `${safeLineKey}-${partIdx++}`;
  const MAX_DEPTH = 20;

  function parse(seg: string, depth: number): React.ReactNode {
    if (depth > MAX_DEPTH || !seg) return seg || null;
    let earliest = -1;
    let markerIdx = -1;
    for (let i = 0; i < INLINE_MARKERS.length; i++) {
      const pos = seg.indexOf(INLINE_MARKERS[i].open);
      if (pos >= 0 && (earliest < 0 || pos < earliest)) {
        earliest = pos;
        markerIdx = i;
      }
    }
    if (earliest < 0) return seg;
    const m = INLINE_MARKERS[markerIdx];
    const before = seg.slice(0, earliest);
    const closeStart = seg.indexOf(m.close, earliest + m.open.length);
    if (closeStart < 0) {
      return (
        <>
          {before ? parse(before, depth + 1) : null}
          <React.Fragment key={key()}>{seg.slice(earliest)}</React.Fragment>
        </>
      );
    }
    const inner = seg.slice(earliest + m.open.length, closeStart);
    const after = seg.slice(closeStart + m.close.length);
    const Wrapper = m.Wrapper;
    return (
      <>
        {before ? parse(before, depth + 1) : null}
        <Wrapper key={key()}>{parse(inner, depth + 1)}</Wrapper>
        {after ? parse(after, depth + 1) : null}
      </>
    );
  }

  return <span className="inline">{parse(text, 0)}</span>;
}

interface SectionContentWithFormattingProps {
  content: string;
  className?: string;
  /** Optional section title hint for smarter rendering (e.g. skills list) */
  sectionTitle?: string;
}

/** True when section is a contact / personal info section */
function isContactSection(title?: string): boolean {
  return !!(title && /personal.?info|contact/i.test(title));
}

/** True when section is work experience so we normalize structure (company · title, dates, bullets) */
function isWorkExperienceSection(title?: string): boolean {
  return !!(title && /\b(work\s+experience|employment|experience|professional\s+experience)\b/i.test(title));
}

/**
 * Split a concatenated contact line into individual fields.
 * Handles LLM output like: "• email@x.com +91-6361... City https://linkedin..."
 * Strips the leading bullet character and splits on phone/URL/email boundaries.
 */
function expandContactLine(line: string): string[] {
  const text = isBulletLine(line) ? bulletContent(line) : line.trim();
  if (!text) return [];
  // Count how many contact-pattern types appear on this line
  const EMAIL_RE = /[\w._%+-]+@[\w.-]+\.[A-Za-z]{2,}/;
  const PHONE_RE = /\+?[\d][\d\s\-()]{6,}/;
  const URL_RE = /https?:\/\/\S+/;
  const matchCount = [EMAIL_RE, PHONE_RE, URL_RE].filter(p => p.test(text)).length;
  if (matchCount < 2) return [text]; // single field — return as-is
  // Split before phone numbers, URLs, and emails that follow a space
  return text
    .split(/(?<=\S)\s+(?=\+[\d])|(?<=\S)\s+(?=https?:\/\/)|(?<=\S)\s+(?=[\w._%+-]+@[\w.-]+\.[A-Za-z])/)
    .map(p => p.trim())
    .filter(Boolean);
}

/** Heuristic: detect a flat list of short items — skills / technologies / tools */
function isSkillsList(lines: string[], sectionTitle?: string): boolean {
  const SKILLS_TITLE_RE = /\b(skill|technolog|tool|framework|language|platform|certif|competen|proficien)/i;
  const titleHint = sectionTitle ? SKILLS_TITLE_RE.test(sectionTitle) : false;
  const nonEmpty = lines.filter(l => l.trim());
  if (nonEmpty.length < 3) return false;
  const hasBullets = nonEmpty.some(l => isBulletLine(l));
  if (hasBullets) return false; // already has bullets — render normally
  const hasLongLine = nonEmpty.some(l => l.trim().length > 60);
  if (hasLongLine) return false; // long descriptive lines → not a plain skills list
  const hasDateLike = nonEmpty.some(l => /\b(19|20)\d{2}\b/.test(l));
  if (hasDateLike) return false;
  const avgLen = nonEmpty.reduce((s, l) => s + l.trim().length, 0) / nonEmpty.length;
  return titleHint || (avgLen <= 35 && nonEmpty.length >= 5);
}

/**
 * Renders section content with bullets, numbered lists, and markdown formatting.
 * Intentionally carries NO hardcoded font-family, font-size, line-height, or color —
 * those are all inherited from the parent .resume-preview--{template} CSS rules so
 * each template can fully control the typography of the body content.
 */
export function SectionContentWithFormatting({ content, className = '', sectionTitle }: SectionContentWithFormattingProps) {
  // Normalize work experience so entries show consistent structure (company · title, dates, bullets)
  const rawContent = content || '';
  const contentToRender = isWorkExperienceSection(sectionTitle)
    ? workExperienceEntriesToDisplayText(parseWorkExperienceContent(rawContent))
    : rawContent;
  const cleaned = preprocessSectionContentForPreview(contentToRender);
  const normalized = cleaned.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split(/\n/);

  // Contact / Personal Information: strip bullets, expand concatenated fields
  if (isContactSection(sectionTitle)) {
    const contactLines = lines.flatMap(line => {
      if (!line.trim()) return [''];
      return expandContactLine(line);
    });
    return (
      <div className={`resume-preview-content break-words overflow-visible ${className}`.trim()}>
        {contactLines.map((line, i) => {
          if (!line.trim()) return <div key={i} className="resume-preview-blank-line" aria-hidden />;
          return (
            <div key={i} className="resume-preview-text-row">
              {renderInlineFormatting(line, `C${i}`)}
            </div>
          );
        })}
      </div>
    );
  }

  // Compact inline rendering for skills/technologies lists
  if (isSkillsList(lines, sectionTitle)) {
    const items = lines.map(l => l.trim()).filter(Boolean);
    return (
      <div className={`resume-preview-content break-words overflow-visible ${className}`.trim()}>
        <div className="resume-preview-skills-inline">
          {items.map((item, i) => (
            <span key={i} className="resume-preview-skill-tag">
              {renderInlineFormatting(item, `SK${i}`)}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`resume-preview-content break-words overflow-visible ${className}`.trim()}>
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <div key={i} className="resume-preview-blank-line" aria-hidden />;
        }
        if (isBulletLine(line)) {
          return (
            <div key={i} className="resume-preview-body-row resume-preview-bullet-row">
              <span className="resume-preview-bullet">•</span>
              <span className="resume-preview-bullet-text">{renderInlineFormatting(bulletContent(line), `L${i}`)}</span>
            </div>
          );
        }
        if (isNumberedLine(line)) {
          const dotMatch = line.match(/^\s*(\d+)\.\s+(.*)$/);
          const parenMatch = line.match(/^\s*(\d+)\)\s+(.*)$/);
          const num = dotMatch ? dotMatch[1] : parenMatch ? parenMatch[1] : '1';
          const restRaw = dotMatch ? dotMatch[2] : parenMatch ? parenMatch[2] : line.replace(/^\s*\d+[.)]\s*/, '');
          const rest = restRaw.trim();
          return (
            <div key={i} className="resume-preview-body-row resume-preview-num-row">
              <span className="resume-preview-bullet">{num}.</span>
              <span className="resume-preview-bullet-text">{renderInlineFormatting(rest, `L${i}`)}</span>
            </div>
          );
        }
        // preserve source indentation as data-driven inline padding only
        const indent = line.match(/^(\s+)/)?.[1].length ?? 0;
        const indentStyle = indent >= 2 ? { paddingLeft: '1.5em' } : indent >= 1 ? { paddingLeft: '0.75em' } : undefined;
        return (
          <div key={i} className="resume-preview-text-row" style={indentStyle}>
            {renderInlineFormatting(trimmed, `L${i}`)}
          </div>
        );
      })}
    </div>
  );
}

/** Split long section content into blocks for card layout. Double-newline or ~600 chars. */
export function splitSectionIntoBlocks(content: string, maxBlockChars = 600): string[] {
  if (!content?.trim()) return [];
  const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  const byDoubleNewline = normalized.split(/\n\n+/);
  const blocks: string[] = [];
  for (const block of byDoubleNewline) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    if (trimmed.length <= maxBlockChars) {
      blocks.push(trimmed);
    } else {
      const lines = trimmed.split(/\n/);
      let current = '';
      for (const line of lines) {
        if (current.length + line.length + 1 > maxBlockChars && current.length > 0) {
          blocks.push(current.trim());
          current = line;
        } else {
          current = current ? current + '\n' + line : line;
        }
      }
      if (current.trim()) blocks.push(current.trim());
    }
  }
  return blocks.length > 0 ? blocks : [normalized];
}
